<template>
  <div class="multi-edit-form">
    <div class="multi-edit-form__header">
      <div>
        <h2 class="multi-edit-form__title">
          {{ $t('Edit Multiple Licenses') }}
        </h2>

        <p class="multi-edit-form__description">
          {{
            $t(
                'Editing {n} selected license(s). Only the enabled fields will be updated.',
                { n: licenses.length }
            )
          }}
        </p>
      </div>

      <div class="multi-edit-form__badge">
        {{ licenses.length }} {{ $t('selected') }}
      </div>
    </div>

    <form @submit.prevent="submitForm">
      <div class="multi-edit-form__grid">
        <div
            v-for="field in fields"
            :key="field.key"
            class="form-field"
            :class="{ 'form-field--wide': field.wide }"
        >
          <label :for="`multi-${field.key}`">
            <input
                v-model="enabled[field.key]"
                type="checkbox"
                class="field-toggle"
            >
            {{ $t(field.label) }}
          </label>

          <!-- Text fields -->
          <input
              v-if="field.type === 'text'"
              :id="`multi-${field.key}`"
              v-model.trim="patch[field.key]"
              type="text"
              autocomplete="off"
              :disabled="!enabled[field.key]"
              :required="enabled[field.key]"
          >

          <!-- Date fields -->
          <input
              v-else-if="field.type === 'date'"
              :id="`multi-${field.key}`"
              v-model="patch[field.key]"
              type="date"
              :disabled="!enabled[field.key]"
              :required="enabled[field.key]"
              :min="field.key === 'expirationDate' ? patch.startDate || null : null"
          >

          <!-- Number fields -->
          <div
              v-else-if="field.type === 'number'"
              class="price-input"
          >
            <input
                :id="`multi-${field.key}`"
                v-model.number="patch[field.key]"
                type="number"
                min="0"
                step="0.01"
                :disabled="!enabled[field.key]"
                :required="enabled[field.key]"
            >
            <span class="price-input__currency">CHF</span>
          </div>

          <!-- License type -->
          <div
              v-else-if="field.type === 'select' && field.key === 'type'"
              class="select-input"
              :class="{ 'select-input--disabled': !enabled[field.key] }"
          >
            <select
                :id="`multi-${field.key}`"
                v-model="patch[field.key]"
                :disabled="!enabled[field.key]"
                :required="enabled[field.key]"
            >
              <option disabled value="">
                {{ $t('Select license type') }}
              </option>
              <option value="viewer">
                {{ $t('Viewer') }}
              </option>
              <option value="editor">
                {{ $t('Editor') }}
              </option>
              <option value="manager">
                {{ $t('Manager') }}
              </option>
              <option value="admin">
                {{ $t('Admin') }}
              </option>
            </select>
          </div>

          <!-- Assigned user -->
          <div
              v-else-if="field.type === 'select' && field.key === 'userId'"
              class="select-input"
              :class="{ 'select-input--disabled': !enabled[field.key] }"
          >
            <searchable-user-select
                v-model="patch[field.key]"
                :users="users"
                :disabled="!enabled[field.key]"
                :placeholder="$t('Search or select a user…')"
            />
          </div>
        </div>
      </div>

      <span
          v-if="expirationDateInvalid"
          class="form-field__error"
      >
        {{ $t('Expiration date must be after the start date.') }}
      </span>

      <div
          v-if="!anyEnabled"
          class="multi-edit-form__hint"
      >
        {{ $t('Enable at least one field to save changes.') }}
      </div>

      <div class="multi-edit-form__actions">
        <button
            type="button"
            class="license-button license-button--secondary"
            @click="$emit('abort')"
        >
          {{ $t('Abort') }}
        </button>

        <button
            type="submit"
            class="license-button license-button--primary"
            :disabled="!anyEnabled || expirationDateInvalid"
        >
          {{ $t('Save changes ({n})', { n: licenses.length }) }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import SearchableUserSelect from './SearchableUserSelect.vue';

const FIELDS = [
  { key: 'name', label: 'Name', type: 'text', wide: true },
  { key: 'startDate', label: 'Start Date', type: 'date' },
  { key: 'expirationDate', label: 'Expiration Date', type: 'date' },
  { key: 'type', label: 'Type', type: 'select' },
  { key: 'userId', label: 'User', type: 'select' }
];

export default {
  name: 'LicenseMultiEditForm',

  components: {
    SearchableUserSelect
  },

  props: {
    licenses: {
      type: Array,
      required: true
    },

    users: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      fields: FIELDS,

      enabled: {
        name: false,
        startDate: false,
        expirationDate: false,
        type: false,
        userId: false
      },

      patch: {
        name: '',
        startDate: '',
        expirationDate: '',
        type: 'viewer',
        userId: ''
      }
    };
  },

  computed: {
    anyEnabled() {
      return Object.values(this.enabled).some(Boolean);
    },

    expirationDateInvalid() {
      if (!this.enabled.startDate || !this.enabled.expirationDate) {
        return false;
      }

      if (!this.patch.startDate || !this.patch.expirationDate) {
        return false;
      }

      return this.patch.expirationDate < this.patch.startDate;
    }
  },

  methods: {
    submitForm() {
      if (!this.anyEnabled || this.expirationDateInvalid) {
        return;
      }

      const changes = {};

      for (const [key, isEnabled] of Object.entries(this.enabled)) {
        if (isEnabled) {
          changes[key] = this.patch[key];
        }
      }

      const updated = this.licenses.map(license => ({
        ...license,
        ...changes
      }));

      this.$emit('submit', updated);
    }
  }
};
</script>

<style scoped lang="scss">
.multi-edit-form {
  max-width: 900px;
  margin: 24px 0;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.multi-edit-form__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}

.multi-edit-form__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.multi-edit-form__description {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
}

.multi-edit-form__badge {
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(242, 201, 76, 0.15);
  color: #f2c94c;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.multi-edit-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
  margin-bottom: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field--wide {
  grid-column: 1 / -1;
}

.form-field label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
}

.field-toggle {
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.form-field input[type="text"],
.form-field input[type="date"],
.form-field input[type="number"],
.form-field select {
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 7px;
  background: rgba(0, 0, 0, 0.2);
  color: inherit;
  font: inherit;
  transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease,
      opacity 0.15s ease,
      background 0.15s ease;
}

.form-field input:disabled,
.form-field select:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.form-field input:not(:disabled):focus,
.form-field select:not(:disabled):focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.65);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
}

.select-input {
  position: relative;
  min-width: 0;
}


.select-input::after {
  position: absolute;
  top: 50%;
  right: 16px;
  width: 8px;
  height: 8px;
  border-right: 2px solid rgba(255, 255, 255, 0.65);
  border-bottom: 2px solid rgba(255, 255, 255, 0.65);
  content: "";
  transform: translateY(-70%) rotate(45deg);
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.select-input--disabled::after {
  opacity: 0.35;
}

.form-field select {
  padding-right: 44px;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.form-field select:disabled {
  cursor: not-allowed;
}

.form-field select option {
  background: #262626;
  color: #ffffff;
}

.form-field__error {
  margin: 0 0 12px;
  color: #ff6b6b;
  font-size: 13px;
}

.multi-edit-form__hint {
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}

.multi-edit-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 22px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.license-button {
  min-width: 140px;
  min-height: 42px;
  padding: 9px 18px;
  border: 1px solid transparent;
  border-radius: 7px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition:
      transform 0.15s ease,
      opacity 0.15s ease,
      background 0.15s ease;
}

.license-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.license-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.license-button--primary {
  background: #ffffff;
  color: #222222;
}

.license-button--primary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.88);
}

.license-button--secondary {
  border-color: rgba(255, 255, 255, 0.2);
  background: transparent;
  color: inherit;
}

.license-button--secondary:hover {
  background: rgba(255, 255, 255, 0.08);
}

@media (max-width: 700px) {
  .multi-edit-form {
    padding: 20px;
  }

  .multi-edit-form__header {
    flex-direction: column;
  }

  .multi-edit-form__grid {
    grid-template-columns: 1fr;
  }

  .form-field--wide {
    grid-column: auto;
  }

  .multi-edit-form__actions {
    flex-direction: column-reverse;
  }

  .license-button {
    width: 100%;
  }
}
</style>
